import { useCallback, useState } from 'react';
import { OrderResponseDto } from 'client/widget-components/ecom/types/EcomSDK';

export const CANCELLATION_REASON_MAX_LENGTH = 500;

export interface CancellationRequest {
    isConfirmVisible: boolean;
    isRequesting: boolean;
    succeeded: boolean;
    error: unknown | null;
    reason: string;
    setReason: (reason: string) => void;
    start: () => void;
    dismiss: () => void;
    confirm: () => Promise<void>;
}

interface Options {
    orderId: string;
    onSuccess?: (order: OrderResponseDto) => void;
}

export function useCancellationRequest({
    orderId,
    onSuccess,
}: Options): CancellationRequest {
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);
    const [succeeded, setSucceeded] = useState(false);
    const [error, setError] = useState<unknown | null>(null);
    const [reason, setReason] = useState('');

    const start = useCallback(() => {
        setError(null);
        setIsConfirmVisible(true);
    }, []);

    const dismiss = useCallback(() => {
        // Ignore while a request is in flight — backing out can't abort the
        // POST, and a late success would cancel the order despite "Keep my
        // order".
        if (isRequesting) return;
        setIsConfirmVisible(false);
        setReason('');
    }, [isRequesting]);

    const confirm = useCallback(async () => {
        if (isRequesting) return;

        setError(null);
        setIsRequesting(true);

        try {
            const updatedOrder = await window._ecomm!.requestOrderCancellation({
                order_id: orderId,
                reason: reason.trim() || undefined,
            });
            setIsConfirmVisible(false);
            setSucceeded(true);
            onSuccess?.(updatedOrder);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setIsRequesting(false);
        }
    }, [isRequesting, orderId, reason, onSuccess]);

    return {
        isConfirmVisible,
        isRequesting,
        succeeded,
        error,
        reason,
        setReason,
        start,
        dismiss,
        confirm,
    };
}
